import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getElementStats } from "core-integration/src/store/element-stats/reducers";
import { elementDialogHide } from "core-integration/src/store/elements/actions";
import {
  getElement,
  getElementDialog,
  getFixturesWithBlanks,
  getHistory,
  getHistoryTotals,
  getSeasonHistory,
} from "core-integration/src/store/elements/reducers";
import {
  IElement,
  IElementFixture,
  IElementFixtureBlank,
  IElementHistory,
} from "core-integration/src/store/elements/types";
import {
  getPlayerData,
  getWatched,
} from "core-integration/src/store/player/reducers";
import {
  addToWatchlist,
  removeFromWatchlist,
} from "core-integration/src/store/player/thunks";
import { proposeElement } from "core-integration/src/store/squad/thunks";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { formatRawAsLocalI18n } from "core-integration/src/utils/datetime";
import { integerToMoney } from "core-integration/src/utils/money";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import { dateLocales } from "../i18n";
import Alert from "./Alert";
import Badge from "./Badge";
import Button from "./Button";
import Dialog from "./Dialog";
import DialogHeading from "./DialogHeading";
import ElementCard from "./ElementCard";
import FixtureForElement from "./FixtureForElement";
import "./OverflowScrolling.css";
import Panel from "./Panel";
import { DrawPrefix, LosePrefix, WinPrefix } from "./ScorePrefix";
import SubHeading from "./SubHeading";
import Table from "./Table";
import TabPanel from "./tabs/TabPanel";
import Tabs from "./tabs/Tabs";
import Tooltip, { TooltipLabel } from "./Tooltip";
import { VisuallyHidden } from "./Utils";
import PlayerWidgetWithAd from "./xStats/PlayerWidget/PlayerWidgetWithAd";

const WidgetWrapper = styled.div`
  padding: ${({ theme }) => theme.space[0]};
  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    padding: ${({ theme }) => theme.space[2]};
    padding-top: ${({ theme }) => theme.space[0]};
  }
`;

const Summary = styled.div`
  padding: ${({ theme }) => theme.space[3]};
`;

const BadgeScoreFlex = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[2]};
  padding-left: -5px;
`;

const EiwBadgeWrapper = styled.div`
  max-width: 25px;
  max-height: 25px;
  padding: ${({ theme }) => `${theme.space[1]} ${theme.space[1]}
    ${theme.space[1]} 0`};
`;

const ScrollTable = styled.div`
  overflow-x: auto;
  width: 100%;
  /* We're adding this in OverflowScrolling.css
	 * because the property gets removed from the
	 * styled component on test */
  /* -webkit-overflow-scrolling: touch; */
`;

const HistoryTable = styled(Table)`
  white-space: nowrap;

  td {
    padding: 10px;
  }
`;

const ElementHeader = styled.div`
  display: flex;
  align-content: center;
  justify-content: space-between;
  line-height: 1;
`;

const ElementMoney = styled.span`
  color: ${({ theme }) => theme.colors.black};
  font-size: 18px;
  font-weight: bold;
  font-family: ${({ theme }) => theme.fonts.base};
  line-height: 1.5;
`;

const TableHeading = styled.h3`
  padding: ${({ theme }) => theme.space[2]};
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 0;
  border-top-left-radius: ${({ theme }) => theme.radii[2]};
  border-top-right-radius: ${({ theme }) => theme.radii[2]};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-family: ${({ theme }) => theme.fonts.base};
  text-transform: none;
  font-size: 16px;
`;

const TablesWrapper = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    display: flex;
    justify-content: space-between;
  }
`;

const TableWrapper = styled.div`
  margin-top: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    flex: 0 0 calc(50% - 1rem);
  }
`;

const NoData = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 0 auto;
  min-height: 108px;
  border-bottom-left-radius: ${({ theme }) => theme.radii[2]};
  border-bottom-right-radius: ${({ theme }) => theme.radii[2]};
  background-color: ${({ theme }) => theme.colors.blueDarker3};
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    min-height: 138px;
  }
`;

const TdBold = styled.td`
  font-weight: bold;
  height: 35px;
`;

interface IElementFixtureFPL extends IElementFixture {
  difficulty: number;
}

interface IOwnProps {
  element: IElement;
}

type Props = IOwnProps;

const ElementDialog = ({ element }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const { t, i18n } = useTranslation();
  const [added, setAdded] = useState(false);

  const originalStats = useSelector(getElementStats);

  // Create ordered stats array for this component only
  const stats = [...originalStats].sort((a, b) => {
    const order = [
      "goals_scored",
      "assists",
      "clean_sheets",
      "goals_conceded",
      "saves",
      "penalties_saved",
      "yellow_cards",
      "red_cards",
      "winning_goals",
      "penalties_missed",
      "own_goals",
      "key_passes",
      "clearances_blocks_interceptions",
      "attacking_bonus",
      "defending_bonus",
    ];
    return order.indexOf(a.name) - order.indexOf(b.name);
  });

  const fixtures = useSelector(
    (state: RootState) =>
      getFixturesWithBlanks(state, element.id) as Array<
        IElementFixtureFPL | IElementFixtureBlank
      >,
  );
  const history = useSelector((state: RootState) =>
    getHistory(state, element.id),
  );
  const historyTotals = useSelector((state: RootState) =>
    getHistoryTotals(state, element.id),
  );

  const player = useSelector(getPlayerData);
  const seasonHistory = useSelector((state: RootState) =>
    getSeasonHistory(state, element.id).slice().reverse(),
  );

  const teams = useSelector(getTeamsById);
  const watched = useSelector(getWatched);

  const closeDialog = useCallback(
    () => dispatch(elementDialogHide()),
    [dispatch],
  );

  const addElement = (elementId: number) => {
    dispatch(proposeElement(elementId));
    closeDialog();
  };

  const renderWatchlistButton = () => {
    if (!player || !player.entry) {
      return null;
    }
    const inWatched = watched.indexOf(element.code) > -1;
    if (inWatched) {
      return added ? (
        <p>
          {t(
            "elementDialog.watchlist.view",
            "View your watchlist on the transfers page",
          )}
        </p>
      ) : (
        <Button
          onClick={() => {
            dispatch(removeFromWatchlist(element.code));
          }}
          width={1}
        >
          {t("elementDialog.watchlist.remove", "Remove from Watchlist")}
        </Button>
      );
    }
    return (
      <Button
        onClick={() => {
          setAdded(true);
          dispatch(addToWatchlist(element.code));
        }}
        width={1}
      >
        {t("elementDialog.watchlist.add", "Add to Watchlist")}
      </Button>
    );
  };

  // Only get the last three in the history
  const lastThreeHistory = history.slice(-3);
  const firstThreeFixtures = fixtures.slice(0, 3);
  // Element info
  const elementFullname = `${element.first_name} ${element.second_name}`;
  const elementCost = `${integerToMoney(element.now_cost, 10)}Mkr`;

  return (
    <Dialog closeDialog={closeDialog}>
      <Dialog.Header closeDialog={closeDialog}>
        <Box py={2} px={3} bg="white">
          <ElementHeader>
            <DialogHeading>{elementFullname}</DialogHeading>
            <ElementMoney>{elementCost}</ElementMoney>
          </ElementHeader>
        </Box>

        <Box p={2} bg="#0e204d">
          <Button
            width={1}
            variant="secondary"
            onClick={() => addElement(element.id)}
          >
            {t("elementDialog.addPlayerCTA", "Add player")}
          </Button>
        </Box>
      </Dialog.Header>
      <Dialog.Body isPadded={false}>
        {element.news && (
          <Box m={2}>
            <Alert type="error">{element.news}</Alert>
          </Box>
        )}

        <Tabs>
          <TabPanel
            label={t("elementDialog.tabs.summary.heading", "Summary")}
            link="Summary"
          >
            <Summary>
              <ElementCard element={element} />
              <TablesWrapper>
                <TableWrapper>
                  <Panel $isFullHeight={true}>
                    <TableHeading>
                      {t(
                        "elementDialog.tabs.summary.recentMatches.title",
                        "Recent matches",
                      )}
                    </TableHeading>
                    {lastThreeHistory.length === 0 ? (
                      <NoData>
                        {t(
                          "elementDialog.tabs.summary.recentMatches.noData",
                          "No recent matches",
                        )}
                      </NoData>
                    ) : (
                      <Table>
                        <thead>
                          <tr>
                            <th>
                              <Tooltip
                                content={t(
                                  "elementDialog.tabs.summary.recentMatches.roundTooltip",
                                  "Round",
                                )}
                              >
                                <TooltipLabel>
                                  {t(
                                    "elementDialog.tabs.summary.recentMatches.roundLabel",
                                    "RD",
                                  )}
                                </TooltipLabel>
                              </Tooltip>
                            </th>
                            <th>
                              {t(
                                "elementDialog.tabs.summary.recentMatches.opponent",
                                "Opponent",
                              )}
                            </th>
                            <th>
                              {t(
                                "elementDialog.tabs.summary.recentMatches.points",
                                "Points",
                              )}
                            </th>
                            <th>
                              <VisuallyHidden>
                                {t(
                                  "elementDialog.tabs.summary.recentMatches.win-loss-draw",
                                  "Result",
                                )}
                              </VisuallyHidden>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {lastThreeHistory.map((h: IElementHistory) => {
                            return (
                              <tr key={h.fixture}>
                                <TdBold>{h.round}</TdBold>

                                <TdBold>
                                  <BadgeScoreFlex>
                                    <EiwBadgeWrapper>
                                      <Badge
                                        team={teams[h.opponent_team]}
                                        isPresentation={true}
                                        sizes="25px"
                                      />
                                    </EiwBadgeWrapper>
                                    {teams[h.opponent_team].name} (
                                    {h.was_home
                                      ? t(
                                          "elementDialog.tabs.history.homeShort",
                                          "H",
                                        )
                                      : t(
                                          "elementDialog.tabs.history.awayShort",
                                          "A",
                                        )}
                                    ){" "}
                                    {h.team_h_score !== null &&
                                      `${h.team_h_score} - ${h.team_a_score}`}
                                  </BadgeScoreFlex>
                                </TdBold>

                                <TdBold>{h.total_points}</TdBold>
                                <TdBold>
                                  {h.team_h_score === h.team_a_score ? (
                                    <DrawPrefix>
                                      {t(
                                        "elementDialog.tabs.history.draw",
                                        "D",
                                      )}
                                    </DrawPrefix>
                                  ) : h.team_h_score > h.team_a_score &&
                                    h.was_home ? (
                                    <WinPrefix>
                                      {t("elementDialog.tabs.history.win", "W")}
                                    </WinPrefix>
                                  ) : h.team_a_score > h.team_h_score &&
                                    !h.was_home ? (
                                    <WinPrefix>
                                      {t("elementDialog.tabs.history.win", "W")}
                                    </WinPrefix>
                                  ) : (
                                    <LosePrefix>
                                      {t(
                                        "elementDialog.tabs.history.loss",
                                        "L",
                                      )}
                                    </LosePrefix>
                                  )}
                                </TdBold>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    )}
                  </Panel>
                </TableWrapper>

                <TableWrapper>
                  <Panel $isFullHeight={true}>
                    <TableHeading>
                      {t(
                        "elementDialog.tabs.summary.upcoming.title",
                        "Upcoming",
                      )}
                    </TableHeading>
                    {firstThreeFixtures.length === 0 ? (
                      <NoData>
                        {t(
                          "elementDialog.tabs.summary.upcoming.noData",
                          "No upcoming matches",
                        )}
                      </NoData>
                    ) : (
                      <Table>
                        <thead>
                          <tr>
                            <th>
                              <Tooltip
                                content={t(
                                  "elementDialog.tabs.summary.upcoming.roundTooltip",
                                  "Round",
                                )}
                              >
                                <TooltipLabel>
                                  {t(
                                    "elementDialog.tabs.summary.upcoming.roundLabel",
                                    "RD",
                                  )}
                                </TooltipLabel>
                              </Tooltip>
                            </th>
                            <th>
                              {t(
                                "elementDialog.tabs.summary.upcoming.opponent",
                                "Opponent",
                              )}
                            </th>
                            <th>
                              {t(
                                "elementDialog.tabs.summary.upcoming.date",
                                "Date",
                              )}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {firstThreeFixtures.map((fixture) =>
                            fixture.code ? (
                              <tr key={fixture.code}>
                                <TdBold>{fixture.event}</TdBold>

                                <TdBold>
                                  <BadgeScoreFlex>
                                    <FixtureForElement
                                      showLongName={true}
                                      showBadge={true}
                                      element={element}
                                      fixture={fixture}
                                      teamsById={teams}
                                    />
                                  </BadgeScoreFlex>
                                </TdBold>

                                <TdBold>
                                  {fixture.kickoff_time ? (
                                    <time dateTime={fixture.kickoff_time}>
                                      {formatRawAsLocalI18n(
                                        fixture.kickoff_time,
                                        dateLocales[i18n.language],
                                        "dd/MM/yy",
                                      )}
                                    </time>
                                  ) : (
                                    <Tooltip
                                      content={t(
                                        "elementDialog.tabs.summary.upcoming.tbcLong",
                                        "Date To Be Confirmed",
                                      )}
                                    >
                                      <span>
                                        <TooltipLabel>
                                          {t(
                                            "elementDialog.tabs.summary.upcoming.tbcShort",
                                            "TBC",
                                          )}
                                        </TooltipLabel>
                                      </span>
                                    </Tooltip>
                                  )}
                                </TdBold>
                              </tr>
                            ) : (
                              <tr key={fixture.event!}>
                                <TdBold>{fixture.event}</TdBold>
                                <TdBold>
                                  {t(
                                    "elementDialog.tabs.summary.upcoming.none",
                                    "None",
                                  )}
                                </TdBold>
                                <TdBold />
                              </tr>
                            ),
                          )}
                        </tbody>
                      </Table>
                    )}
                  </Panel>
                </TableWrapper>
              </TablesWrapper>
            </Summary>
          </TabPanel>

          <TabPanel
            label={t("elementDialog.tabs.history.heading", "History")}
            link="History"
          >
            <Box p={3}>
              <SubHeading>
                {t("elementDialog.tabs.history.subHeading", "This Season")}
              </SubHeading>
            </Box>
            <Box>
              {history.length ? (
                <ScrollTable className="ism-overflow-scroll">
                  <HistoryTable>
                    <thead>
                      <tr>
                        <th>
                          {t("elementDialog.tabs.history.roundShort", "RD")}
                        </th>
                        <th>
                          {t("elementDialog.tabs.history.opponentShort", "OPP")}
                        </th>
                        <th>
                          {t("elementDialog.tabs.history.pointsShort", "PTS")}
                        </th>
                        {stats.map((s) => (
                          <th key={s.name}>
                            <Tooltip content={s.label}>
                              <TooltipLabel>
                                {s.label
                                  .split(" ")
                                  .map((w) => w[0])
                                  .join("")
                                  .toUpperCase()}
                              </TooltipLabel>
                            </Tooltip>
                          </th>
                        ))}
                        <th>
                          {t(
                            "elementDialog.tabs.history.netTransfersShort",
                            "NT",
                          )}
                        </th>
                        <th>
                          {t(
                            "elementDialog.tabs.history.selectedByShort",
                            "SB",
                          )}
                        </th>
                        <th>kr</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h) => (
                        <tr key={h.fixture}>
                          <td>{h.round}</td>
                          <td>
                            {teams[h.opponent_team].name} (
                            {h.was_home
                              ? t("elementDialog.tabs.history.homeShort", "H")
                              : t("elementDialog.tabs.history.awayShort", "A")}
                            ){" "}
                            {h.team_h_score !== null &&
                              `${h.team_h_score} - ${h.team_a_score}`}
                          </td>
                          <td>{h.total_points}</td>
                          {stats.map((s) => (
                            <td key={s.name}>{h[s.name]}</td>
                          ))}
                          <td>{h.transfers_balance}</td>
                          <td>{h.selected}</td>
                          <td>kr{integerToMoney(h.value, 10)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={2}>
                          {t("elementDialog.tabs.history.totals", "Totals")}
                        </td>
                        <td>{historyTotals.total_points}</td>
                        {stats.map((s) => (
                          <td key={s.name}>{historyTotals[s.name]}</td>
                        ))}
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                      </tr>
                    </tbody>
                  </HistoryTable>
                </ScrollTable>
              ) : (
                <Box px={3}>
                  <p>
                    {t(
                      "elementDialog.tabs.history.noData",
                      "Data will appear here once the season is underway.",
                    )}
                  </p>
                </Box>
              )}
            </Box>

            {seasonHistory && seasonHistory.length > 0 && (
              <>
                <Box p={3}>
                  <SubHeading>
                    {t(
                      "elementDialog.tabs.history.previousSeasons",
                      "Previous Seasons",
                    )}
                  </SubHeading>
                </Box>
                <Box>
                  <ScrollTable>
                    <HistoryTable>
                      <thead>
                        <tr>
                          <th>
                            {t("elementDialog.tabs.history.season", "Season")}
                          </th>
                          <th>
                            <Tooltip
                              content={t(
                                "elementDialog.tabs.history.points",
                                "Points",
                              )}
                            >
                              <TooltipLabel>TP</TooltipLabel>
                            </Tooltip>
                          </th>
                          {stats.map((s) => (
                            <th key={s.name}>
                              <Tooltip content={s.label}>
                                <TooltipLabel>
                                  {s.label
                                    .split(" ")
                                    .map((w) => w[0])
                                    .join("")
                                    .toUpperCase()}
                                </TooltipLabel>
                              </Tooltip>
                            </th>
                          ))}
                          <th>
                            <abbr
                              title={t<string>(
                                "elementDialog.tabs.history.priceAtStart",
                                "Price at start of season",
                              )}
                            >
                              krS
                            </abbr>
                          </th>
                          <th>
                            <abbr
                              title={t<string>(
                                "elementDialog.tabs.history.priceAtEnd",
                                "Price at end of season",
                              )}
                            >
                              krE
                            </abbr>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {seasonHistory.map((h) => (
                          <tr key={h.season_name}>
                            <td>{h.season_name}</td>
                            <td>{h.total_points}</td>
                            {stats.map((s) => (
                              <td key={s.name}>{h[s.name]}</td>
                            ))}
                            <td>kr{integerToMoney(h.start_cost, 10)}</td>
                            <td>kr{integerToMoney(h.end_cost, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </HistoryTable>
                  </ScrollTable>
                </Box>
              </>
            )}
          </TabPanel>

          <TabPanel
            label={t("elementDialog.tabs.schedule.heading", "Schedule")}
            link="Schedule"
          >
            <Box p={3}>
              <SubHeading>
                {t("elementDialog.tabs.schedule.subHeading", "This Season")}
              </SubHeading>
            </Box>
            <Box>
              <Table>
                <thead>
                  <tr>
                    <th>{t("elementDialog.tabs.schedule.date", "Date")}</th>
                    <th>{t("elementDialog.tabs.schedule.roundShort", "RD")}</th>
                    <th>
                      {t("elementDialog.tabs.schedule.opponent", "Opponent")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fixtures.map((fixture) =>
                    fixture.code ? (
                      <tr key={fixture.code}>
                        <td>
                          {fixture.kickoff_time ? (
                            <time dateTime={fixture.kickoff_time}>
                              {formatRawAsLocalI18n(
                                fixture.kickoff_time,
                                dateLocales[i18n.language],
                              )}
                            </time>
                          ) : (
                            <Tooltip
                              content={t(
                                "elementDialog.tabs.schedule.tbcLong",
                                "Date To Be Confirmed",
                              )}
                            >
                              <span>
                                <TooltipLabel>
                                  {t(
                                    "elementDialog.tabs.schedule.tbcShort",
                                    "TBC",
                                  )}
                                </TooltipLabel>
                              </span>
                            </Tooltip>
                          )}
                        </td>
                        <td>{fixture.event}</td>

                        <td>
                          <FixtureForElement
                            element={element}
                            fixture={fixture}
                            teamsById={teams}
                          />
                        </td>
                      </tr>
                    ) : (
                      <tr key={fixture.event!}>
                        <td>&nbsp;</td>
                        <td>{fixture.event}</td>
                        <td>{t("elementDialog.tabs.schedule.none", "None")}</td>
                      </tr>
                    ),
                  )}
                </tbody>
              </Table>
            </Box>
          </TabPanel>
          <TabPanel
            label={t("elementDialog.tabs.xStats.heading", "xStats")}
            link="xStats"
          >
            <Box p={3}>
              <ElementCard element={element} />
            </Box>
            <WidgetWrapper>
              <PlayerWidgetWithAd
                selectorId={"element-dialog-player-widget"}
                element={element}
                showWidgetSummary={false}
              />
            </WidgetWrapper>
          </TabPanel>
        </Tabs>

        <Box mx={2} my={3}>
          {renderWatchlistButton()}
        </Box>
      </Dialog.Body>
    </Dialog>
  );
};

const ElementDialogContainer = () => {
  const elementId = useSelector(getElementDialog);
  const element = useSelector((state: RootState) =>
    getElement(state, elementId),
  );
  return element ? <ElementDialog element={element} /> : null;
};

export default ElementDialogContainer;
